import { HD_BODYGRAPH_CENTERS } from '../hd-bodygraph-metadata';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { HdPlanetKey } from '../planet-grid/hd-planet-display-config';

/**
 * Vrij JSON-blok met extra momentgegevens (`Chart.MomentExtraJson`, spec
 * `docs/superpowers/specs/2026-10-22-mind-logboek-design.md` §3.2). Het schema
 * is frontend-owned: de backend bewaakt enkel de lengte (2000) en dat het een
 * JSON-object is. Elk soort moment krijgt een eigen sleutel (`mind`, later bv.
 * Transit Watch), zodat ze elkaar niet raken.
 */
export interface HdMomentExtra {
  readonly mind?: HdMindMomentExtra;
}

/** Plafond per notitie (ronde 2, 2026-10-24): meer dan een paar conditioneerders zegt niets meer. */
export const HD_MIND_MAX_CONDITIONERS = 4;

/** Mind-notitie: waar het denken via de openheid binnenkomt en wie of wat het conditioneert. */
export interface HdMindMomentExtra {
  /** Open (of gedefinieerd) centrum waar de conditionering binnenkomt. */
  readonly center?: HdBodygraphCenter;
  /** Optioneel: een poort van dat centrum. */
  readonly gate?: number;
  /**
   * Conditioneerders (max. `HD_MIND_MAX_CONDITIONERS`): transits op het tijdstip en/of een
   * persoon. De eerste versie bewaarde er één onder `conditioner`; de parser leest die
   * nog en zet ze in deze lijst.
   */
  readonly conditioners?: readonly HdMindConditioner[];
}

export interface HdMindTransitConditioner {
  readonly kind: 'transit';
  readonly planetKey: HdPlanetKey;
  readonly gate: number;
  readonly line?: number;
}

export interface HdMindPersonConditioner {
  readonly kind: 'person';
  readonly chartId: string;
  readonly chartName: string;
  /** Poorten waarmee die persoon het centrum bij de gebruiker invult (mag leeg zijn). */
  readonly gates: readonly number[];
}

export type HdMindConditioner = HdMindTransitConditioner | HdMindPersonConditioner;

const CENTERS = new Set<string>(HD_BODYGRAPH_CENTERS);

/**
 * Tolerante parser: ongeldige JSON of een niet-object geeft `{}`; onbekende
 * sleutels blijven bewaard (round-trip), het `mind`-blok wordt veldsgewijs
 * gevalideerd zodat een kapotte waarde nooit de UI breekt.
 */
export function parseHdMomentExtra(json: string | null | undefined): HdMomentExtra {
  if (!json?.trim()) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return {};
  }
  if (!isRecord(raw)) return {};
  const mind = parseMind(raw['mind']);
  const rest: Record<string, unknown> = { ...raw };
  delete rest['mind'];
  return { ...rest, ...(mind ? { mind } : {}) } as HdMomentExtra;
}

/** Compacte serialisatie; `null` wanneer er niets te bewaren valt (= kolom wissen). */
export function joinHdMomentExtra(extra: HdMomentExtra | null | undefined): string | null {
  if (!extra) return null;
  const clean: Record<string, unknown> = { ...extra };
  if (extra.mind != null) {
    const mind = compactMind(extra.mind);
    if (mind) clean['mind'] = mind;
    else delete clean['mind'];
  }
  return Object.keys(clean).length === 0 ? null : JSON.stringify(clean);
}

/** Enkel de mind-gegevens uit een bewaard moment, of null. */
export function parseHdMindMomentExtra(json: string | null | undefined): HdMindMomentExtra | null {
  return parseHdMomentExtra(json).mind ?? null;
}

function parseMind(value: unknown): HdMindMomentExtra | null {
  if (!isRecord(value)) return null;
  const center = typeof value['center'] === 'string' && CENTERS.has(value['center']) ? (value['center'] as HdBodygraphCenter) : undefined;
  const gate = isGate(value['gate']) ? (value['gate'] as number) : undefined;
  const conditioners = parseConditioners(value);
  if (center == null && gate == null && conditioners.length === 0) return null;
  return {
    ...(center != null ? { center } : {}),
    ...(gate != null ? { gate } : {}),
    ...(conditioners.length > 0 ? { conditioners } : {}),
  };
}

/** `conditioners` (lijst), met het enkelvoud `conditioner` van de eerste versie als terugval. */
function parseConditioners(value: Record<string, unknown>): HdMindConditioner[] {
  const raw = Array.isArray(value['conditioners']) ? value['conditioners'] : [value['conditioner']];
  return raw
    .map(entry => parseConditioner(entry))
    .filter((entry): entry is HdMindConditioner => entry != null)
    .slice(0, HD_MIND_MAX_CONDITIONERS);
}

function parseConditioner(value: unknown): HdMindConditioner | null {
  if (!isRecord(value)) return null;
  if (value['kind'] === 'transit') {
    if (typeof value['planetKey'] !== 'string' || !isGate(value['gate'])) return null;
    const line = value['line'];
    return {
      kind: 'transit',
      planetKey: value['planetKey'] as HdPlanetKey,
      gate: value['gate'] as number,
      ...(typeof line === 'number' && Number.isInteger(line) && line >= 1 && line <= 6 ? { line } : {}),
    };
  }
  if (value['kind'] === 'person') {
    if (typeof value['chartId'] !== 'string' || !value['chartId']) return null;
    const gates = Array.isArray(value['gates']) ? value['gates'].filter(isGate) as number[] : [];
    return {
      kind: 'person',
      chartId: value['chartId'],
      chartName: typeof value['chartName'] === 'string' ? value['chartName'] : '',
      gates,
    };
  }
  return null;
}

function compactMind(mind: HdMindMomentExtra): HdMindMomentExtra | null {
  const result: Record<string, unknown> = {};
  if (mind.center != null) result['center'] = mind.center;
  if (mind.gate != null) result['gate'] = mind.gate;
  if (mind.conditioners != null && mind.conditioners.length > 0) {
    result['conditioners'] = mind.conditioners.slice(0, HD_MIND_MAX_CONDITIONERS);
  }
  return Object.keys(result).length === 0 ? null : (result as HdMindMomentExtra);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isGate(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 64;
}

/**
 * Leesbare vorm van één conditioneerder, voor export en voor "Zet in tekst" in
 * de dialoog: "Zon 64.3" (planeetnaam via `planetName`) of "Ann (21, 40)".
 */
export function hdMindConditionerText(conditioner: HdMindConditioner, planetName: (planetKey: HdPlanetKey) => string): string {
  if (conditioner.kind === 'transit') {
    return `${planetName(conditioner.planetKey)} ${conditioner.gate}${conditioner.line != null ? `.${conditioner.line}` : ''}`;
  }
  return conditioner.gates.length > 0 ? `${conditioner.chartName} (${conditioner.gates.join(', ')})` : conditioner.chartName;
}
